import React, { useState, useEffect, useMemo } from 'react';
import {
  FaSearch,
  FaChartBar,
  FaChartLine,
  FaChartPie,
  FaChartArea,
  FaTable,
  FaPlus,
  FaArrowLeft,
  FaArrowRight,
  FaEye,
  FaCog,
  FaFilter,
  FaRefresh,
  FaTachometerAlt,
  FaRegFileAlt
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import ReportChart from '../reports/charts/ReportChart';
import { ChartDisplay } from '../reports/charts';
import './css/WidgetBuilder.css';

const REPORT_COLUMN_OPTIONS = [
  { value: 3, label: '3 Columns (25%)' },
  { value: 4, label: '4 Columns (33%)' },
  { value: 6, label: '6 Columns (50%)' },
  { value: 8, label: '8 Columns (67%)' },
  { value: 9, label: '9 Columns (75%)' },
  { value: 12, label: '12 Columns (Full Width)' }
];

const DASHBOARD_COLUMN_VALUES = Array.from({ length: 12 }, (_, index) => index + 1);
const ROW_OPTIONS = Array.from({ length: 25 }, (_, index) => index + 1);

const getDashboardColumnLabel = (value) => {
  if (value === 1) return '1 Column';
  if (value === 6) return '6 Columns (Default)';
  if (value === 12) return '12 Columns (Full Width)';
  return `${value} Columns`;
};

const getRowOptionLabel = (value) => {
  if (value === 1) return '1 Row';
  if (value === 4) return '4 Rows (Default)';
  return `${value} Rows`;
};

const getConfigStep = (typeValue, includeFullValue) => {
  if (typeValue === 'dashboard') {
    return 3;
  }

  if (typeValue === 'report') {
    return 4;
  }

  return includeFullValue ? 3 : 4;
};

const WidgetBuilder = ({
  isOpen,
  onClose,
  dashboardId,
  onAddWidget,
  onPreviewWidget,
  editingWidget
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [widgetType, setWidgetType] = useState('report');
  const [dashboards, setDashboards] = useState([]);
  const [filteredDashboards, setFilteredDashboards] = useState([]);
  const [dashboardSearchTerm, setDashboardSearchTerm] = useState('');
  const [dashboardsLoading, setDashboardsLoading] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportCharts, setReportCharts] = useState([]);
  const [selectedChart, setSelectedChart] = useState(null);
  const [showChartBuilder, setShowChartBuilder] = useState(false);
  const [includeFullReport, setIncludeFullReport] = useState(false);

  // Report data processing states (following ReportDataViewer pattern)
  const [reportInfo, setReportInfo] = useState(null);
  const [selectedFields, setSelectedFields] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [summarizedFields, setSummarizedFields] = useState({});
  const [previewData, setPreviewData] = useState([]);
  const [groupingInfo, setGroupingInfo] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [widgetConfig, setWidgetConfig] = useState({
    name: '',
    chart_type: 'bar',
    column_span: 6, // Default to half of 12-column grid
    row_span: 4, // Default height
    refresh_rate: 300, // 5 minutes
    show_filters: true,
    show_export: true,
    sort_order: '', // Sort order number
    chart_config: {},
    type: 'report'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      resetBuilder();
      return;
    }

    resetBuilder();

    const editingTypeRaw = editingWidget ? (editingWidget.type || editingWidget.chart_type || '') : 'report';
    const resolvedType = editingTypeRaw === 'dashboard' ? 'dashboard' : 'report';
    const isChartWidget = editingTypeRaw === 'chart';

    setWidgetType(resolvedType);
    setIncludeFullReport(resolvedType === 'report' ? !isChartWidget : false);

    if (resolvedType === 'dashboard') {
      loadDashboards();
    } else {
      loadReports();
    }

    if (editingWidget) {
      setWidgetConfig({
        name: editingWidget.name || '',
        chart_type: editingWidget.chart_type || 'bar',
        column_span: editingWidget.column_span || 6,
        row_span: editingWidget.row_span || 4,
        refresh_rate: editingWidget.refresh_rate || 300,
        show_filters: editingWidget.show_filters !== undefined ? editingWidget.show_filters : true,
        show_export: editingWidget.show_export !== undefined ? editingWidget.show_export : true,
        sort_order: editingWidget.sort_order || '',
        chart_config: editingWidget.chart_config || {}
      });

      if (resolvedType === 'dashboard') {
        setSelectedDashboard({
          id: editingWidget.parent_id,
          name: editingWidget.dashboard_label || editingWidget.name,
          description: editingWidget.dashboard_description || ''
        });
        setCurrentStep(3);
      } else {
        const reportIdForWidget = editingWidget.parent_id || editingWidget.report_id;
        setSelectedReport({
          id: reportIdForWidget,
          report_label: editingWidget.report_label || editingWidget.name?.split(' - ')[0] || 'Selected Report',
          name: editingWidget.report_label || editingWidget.name?.split(' - ')[0] || 'Selected Report'
        });

        loadReportData(reportIdForWidget);

        if (editingTypeRaw === 'report') {
          setIncludeFullReport(true);
          setSelectedChart(null);
          setCurrentStep(getConfigStep('report', true));
        } else {
          setIncludeFullReport(false);
          const chartData = editingWidget.chart_config || editingWidget.selectedChart || {};
          setSelectedChart(chartData);
          setCurrentStep(getConfigStep('report', false));
        }
      }
    } else {
      setCurrentStep(1);
    }
  }, [isOpen, editingWidget]);

  useEffect(() => {
    filterReports();
  }, [reports, searchTerm]);

  useEffect(() => {
    filterDashboards();
  }, [dashboards, dashboardSearchTerm]);

  useEffect(() => {
    const maxStep = getConfigStep(widgetType, includeFullReport);
    if (currentStep > maxStep) {
      setCurrentStep(maxStep);
    }
  }, [widgetType, includeFullReport]);

  useEffect(() => {
    if (widgetType === 'dashboard' && selectedDashboard && dashboards.length) {
      const match = dashboards.find(d => d.id === selectedDashboard.id);
      if (match && (match.name !== selectedDashboard.name || match.label !== selectedDashboard.label)) {
        setSelectedDashboard(match);
      }
    }
  }, [widgetType, dashboards, selectedDashboard]);

  const resetBuilder = () => {
    setCurrentStep(1);
    setWidgetType('report');
    setSelectedDashboard(null);
    setDashboards([]);
    setFilteredDashboards([]);
    setDashboardSearchTerm('');
    setSelectedReport(null);
    setSelectedChart(null);
    setShowChartBuilder(false);
    setIncludeFullReport(true);
    setSearchTerm('');
    setSelectedFields([]);
    setSummarizedFields({});
    setSelectedGroups([]);
    setPreviewData([]);
    setGroupingInfo(null);
    setWidgetConfig({
      name: '',
      chart_type: 'bar',
      column_span: 6, // Default to half of 12-column grid
      row_span: 4, // Default height
      refresh_rate: 300,
      show_filters: true,
      show_export: true,
      sort_order: '', // Sort order number
      chart_config: {},
      type: 'report'
    });
  };

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const reportApi = new DataApi('reports'); // Fixed: use 'reports' (plural) to match backend route
      console.log('Loading reports from API...');
      const response = await reportApi.fetchAll();

      console.log('Reports API response:', response.data);

      if (response.data.success) {
        setReports(response.data.data);
        console.log('Reports loaded successfully:', response.data.data.length, 'reports');
      } else {
        setError('Failed to load reports');
        console.error('API returned success: false', response.data);
      }
    } catch (err) {
      console.error('Error loading reports:', err);
      setError('Error loading reports. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadDashboards = async () => {
    try {
      setDashboardsLoading(true);
      setError(null);
      const dashboardApi = new DataApi('dashboard');
      console.log('Loading dashboards from API...');
      const response = dashboardApi.fetchAll ? await dashboardApi.fetchAll() : await dashboardApi.get('/');
      const data = response?.data?.data || response?.data || [];
      setDashboards(data);
      console.log('Dashboards loaded successfully:', data.length);
    } catch (err) {
      console.error('Error loading dashboards:', err);
      setError('Error loading dashboards. Please try again.');
    } finally {
      setDashboardsLoading(false);
    }
  };

  const filterReports = () => {
    if (!searchTerm.trim()) {
      setFilteredReports(reports);
    } else {
      const filtered = reports.filter(report =>
        report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (report.description && report.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredReports(filtered);
    }
  };

  const filterDashboards = () => {
    if (!dashboardSearchTerm.trim()) {
      setFilteredDashboards(dashboards);
    } else {
      const filtered = dashboards.filter(dashboard =>
        (dashboard.name || '').toLowerCase().includes(dashboardSearchTerm.toLowerCase()) ||
        (dashboard.description && dashboard.description.toLowerCase().includes(dashboardSearchTerm.toLowerCase()))
      );
      setFilteredDashboards(filtered);
    }
  };


  const handleReportSelect = async (report) => {
    console.log('Report selected:', report);
    const reportDetails = {
      id: report.id,
      report_label: report.report_label || report.name,
      name: report.report_label || report.name,
      description: report.description
    };
    setSelectedReport(reportDetails);
    setError(null);
    setIncludeFullReport(false);
    setWidgetConfig(prev => ({
      ...prev,
      name: prev.name || `${reportDetails.report_label} Widget`,
      chart_type: 'bar',
      type: 'chart'
    }));

    try {
      await loadReportData(report.id);
    } catch (err) {
      console.error('Error loading report data:', err);
      setError('Error loading report data. Please try again.');
    }

    setCurrentStep(3);
  };

  // Load report data following ReportDataViewer pattern
  const loadReportData = async (reportId) => {
    setPreviewLoading(true);
    setError(null);

    try {
      // First, get the report configuration (same as ReportDataViewer)
      const reportBuilderApi = new DataApi('reportbuilder');
      const reportResponse = await reportBuilderApi.get(`/${reportId}`);

      if (!reportResponse?.data?.success) {
        throw new Error('Failed to load report configuration');
      }

      const report = reportResponse.data.data;
      console.log('WidgetBuilder - Loaded report for viewer:', report);

      // Set report information (same as ReportDataViewer)
      setReportInfo({
        id: report.id,
        name: report.name,
        report_label: report.report_label,
        description: report.description,
        object_name: report.object_name,
        object_label: report.object_label,
        report_type: report.report_type,
        is_public: report.is_public,
        filter_logic: report.filter_logic,
        filter_custom_logic: report.filter_custom_logic,
        report_objects: report.report_objects || []
      });

      // Process report fields and groups (same as ReportDataViewer)
      if (report.fields && report.fields.length > 0) {
        const fields = report.fields.map(field => ({
          id: field.field_config_id,
          field_name: field.field_name,
          field_label: field.field_label,
          data_type: field.data_type,
          object_name: field.object_name,
          is_aggregated: field.is_aggregated,
          aggregate_function: field.aggregate_function,
          is_grouped: field.is_grouped,
          sort_order: field.sort_order
        }));

        // Helper function to convert database aggregation values to dropdown values
        const convertAggregationValue = (dbValue) => {
          const mapping = {
            'average': 'avg',
            'sum': 'sum',
            'count': 'count',
            'min': 'min',
            'max': 'max'
          };
          return mapping[dbValue] || dbValue;
        };

        // Process fields and create summarized fields
        const selectedFieldsList = [];
        const groups = [];
        const summarizedFieldsData = {};

        fields.forEach(field => {
          selectedFieldsList.push(field);

          if (field.is_grouped) {
            groups.push(field);
          }

          if (field.is_aggregated && field.aggregate_function) {
            summarizedFieldsData[field.id] = {
              aggregation: convertAggregationValue(field.aggregate_function)
            };
          }
        });

        setSelectedFields(selectedFieldsList);
        setSelectedGroups(groups);
        setSummarizedFields(summarizedFieldsData);

        // Load charts from the report data (same as ReportDataViewer)
        console.log('WidgetBuilder - Report charts data:', report.charts);
        console.log('WidgetBuilder - Is charts array?', Array.isArray(report.charts));
        console.log('WidgetBuilder - Charts length:', report.charts ? report.charts.length : 'undefined');

        if (report.charts && Array.isArray(report.charts) && report.charts.length > 0) {
          console.log('WidgetBuilder - Setting charts from report data:', report.charts);
          setReportCharts(report.charts);
        } else {
          console.log('WidgetBuilder - No charts found in report data');
          setReportCharts([]);
        }
      }

      // Fetch actual report data for charts (same as ReportDataViewer)
      await fetchReportData(report);

    } catch (error) {
      console.error('Error loading report data:', error);
      setError('Error loading report data. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Fetch actual report data (same as ReportDataViewer)
  const fetchReportData = async (report, filters = {}) => {
    setPreviewLoading(true);
    console.log('WidgetBuilder - fetchReportData:', {
      reportId: report.id,
      limit: 10, // Limit to 10 records for widget preview
      filters: filters
    });

    try {
      // Use the same route as ReportDataViewer to fetch actual report data
      const dataApi = new DataApi('object');
      const response = await dataApi.post('/view/report', {
        reportId: report.id,
        limit: 10000, // Limit to 10 records for widget preview
        filters: filters
      });

      if (response?.data?.success) {
        const rawData = response.data.data;
        const grouping = response.data.grouping;

        console.log('WidgetBuilder - Report data loaded:', rawData);
        console.log('WidgetBuilder - Grouping info:', grouping);

        setPreviewData(rawData);
        setGroupingInfo(grouping);

      } else {
        throw new Error(response?.data?.message || 'Failed to fetch report data');
      }

    } catch (err) {
      console.error('WidgetBuilder - Error fetching report data:', err);
      setError('Error fetching report data. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Create summarized data for charts (same as ReportDataViewer)
  const createSummarizedDataForCharts = () => {
    if (!groupingInfo || !groupingInfo.hasGrouping || !previewData || previewData.length === 0) {
      return {};
    }

    const summarizedData = {};
    const groupedFields = groupingInfo.groupedFields;
    const nonGroupedFields = selectedFields.filter(field =>
      !groupedFields.some(groupField =>
        groupField.field_name === field.field_name &&
        groupField.object_name === field.object_name
      )
    );

    // Initialize metadata
    summarizedData._metadata = {
      group_fields: {},
      summarized_fields: {},
      report_info: {
        total_records: previewData.length,
        group_count: groupedFields.length,
        summarized_field_count: Object.keys(summarizedFields).length,
        extraction_timestamp: new Date().toISOString(),
        data_structure_version: '2.0'
      }
    };

    // Add metadata for grouped fields
    groupedFields.forEach(field => {
      summarizedData._metadata.group_fields[field.field_name] = {
        field_id: field.id,
        field_name: field.field_name,
        field_label: field.field_label,
        data_type: field.data_type,
        object_name: field.object_name,
        field_type: field.field_type
      };
    });

    // Add metadata for summarized fields
    nonGroupedFields.forEach(field => {
      const summarizedField = summarizedFields[field.id];
      if (summarizedField) {
        summarizedData._metadata.summarized_fields[field.field_name] = {
          field_id: field.id,
          field_name: field.field_name,
          field_label: field.field_label,
          data_type: field.data_type,
          object_name: field.object_name,
          aggregation: summarizedField.aggregation
        };
      }
    });

    // Always add record_count to summarized_fields metadata if there are grouped fields
    console.log('WidgetBuilder - Adding record_count to metadata. Grouped fields count:', groupedFields.length);
    if (groupedFields.length > 0) {
      summarizedData._metadata.summarized_fields['record_count'] = {
        field_id: 'record_count',
        field_name: 'record_count',
        field_label: 'Record Count',
        data_type: 'number',
        object_name: 'virtual',
        aggregation: 'count',
        is_virtual: true
      };
      console.log('WidgetBuilder - Added record_count to summarized_fields metadata');
    } else {
      console.log('WidgetBuilder - No grouped fields found, skipping record_count');
    }

    // Process all grouped fields, not just the first one
    if (groupedFields.length > 0) {
      // Process each grouped field to create data structures for all possible xAxisFields
      groupedFields.forEach(primaryGroupField => {
        const groupFieldName = primaryGroupField.field_name;

        // Get unique group values for this specific field
        const groupValues = new Set();
        previewData.forEach(row => {
          const tableName = primaryGroupField.object_name || 'main';
          const aliasedFieldName = `${tableName}_${primaryGroupField.field_name}`;
          const fieldData = row[aliasedFieldName] || row[primaryGroupField.field_name];

          let groupValue;
          if (typeof fieldData === 'object' && fieldData !== null) {
            groupValue = fieldData.value || fieldData.displayValue;
          } else {
            groupValue = fieldData;
          }

          if (groupValue !== null && groupValue !== undefined && groupValue !== '') {
            groupValues.add(groupValue);
          }
        });

        // Initialize each summarized field in the root level (only once)
        if (groupFieldName === groupedFields[0].field_name) {
          nonGroupedFields.forEach(field => {
            const summarizedField = summarizedFields[field.id];
            if (summarizedField) {
              if (!summarizedData[field.field_name]) {
                summarizedData[field.field_name] = {};
              }
            }
          });

          // Always initialize record_count field
          if (!summarizedData['record_count']) {
            summarizedData['record_count'] = {};
          }
        }

        // Calculate aggregated values for each group value
        groupValues.forEach(groupValue => {
          const rowsForGroup = previewData.filter(row => {
            const tableName = primaryGroupField.object_name || 'main';
            const aliasedFieldName = `${tableName}_${primaryGroupField.field_name}`;
            const fieldData = row[aliasedFieldName] || row[primaryGroupField.field_name];

            let rowGroupValue;
            if (typeof fieldData === 'object' && fieldData !== null) {
              rowGroupValue = fieldData.value || fieldData.displayValue;
            } else {
              rowGroupValue = fieldData;
            }

            return rowGroupValue === groupValue;
          });

          // Calculate aggregated values for each non-grouped field
          nonGroupedFields.forEach(field => {
            const summarizedField = summarizedFields[field.id];
            if (summarizedField) {
              const aggregation = summarizedField.aggregation;
              const aggregatedValue = calculateAggregation(rowsForGroup, field, aggregation);

              // For charts, we need raw numeric values, not formatted strings
              let chartValue = aggregatedValue;
              if (field.data_type === 'currency' && typeof aggregatedValue === 'string') {
                // Extract numeric value from currency string (e.g., "₹35,000.00" -> 35000)
                const numericValue = parseFloat(aggregatedValue.replace(/[₹,]/g, ''));
                chartValue = isNaN(numericValue) ? 0 : numericValue;
                console.log(`WidgetBuilder - Currency conversion: "${aggregatedValue}" -> ${chartValue}`);
              } else if (field.data_type === 'number' || field.data_type === 'decimal') {
                // Ensure numeric values are properly parsed
                const numericValue = parseFloat(aggregatedValue);
                chartValue = isNaN(numericValue) ? 0 : numericValue;
                console.log(`WidgetBuilder - Numeric conversion: "${aggregatedValue}" -> ${chartValue}`);
              }

              // Structure: { "salary": { "Store": 35000, "FrontDesk": 203000 } }
              summarizedData[field.field_name][groupValue] = chartValue;
            }
          });

          // Calculate record count for this group
          const recordCount = rowsForGroup.length;
          summarizedData['record_count'][groupValue] = recordCount;
          console.log(`WidgetBuilder - Record count for group "${groupValue}": ${recordCount} (from ${rowsForGroup.length} records)`);
        });

        // Create the main structure that ChartDisplay expects for this group field
        summarizedData[groupFieldName] = {};
        groupValues.forEach(groupValue => {
          summarizedData[groupFieldName][groupValue] = {};
          nonGroupedFields.forEach(field => {
            const summarizedField = summarizedFields[field.id];
            if (summarizedField && summarizedData[field.field_name][groupValue] !== undefined) {
              // ChartDisplay expects: { "salary": { "value": 35000 } }
              summarizedData[groupFieldName][groupValue][field.field_name] = {
                value: summarizedData[field.field_name][groupValue]
              };
            }
          });

          // Always add record_count to the main structure
          if (summarizedData['record_count'][groupValue] !== undefined) {
            summarizedData[groupFieldName][groupValue]['record_count'] = {
              value: summarizedData['record_count'][groupValue]
            };
            console.log(`WidgetBuilder - Added record_count to main structure for group "${groupValue}": ${summarizedData['record_count'][groupValue]}`);
          } else {
            console.log(`WidgetBuilder - No record_count found for group "${groupValue}"`);
          }
        });
      });
    }

    console.log('WidgetBuilder - Created summarized data for charts:', summarizedData);
    return summarizedData;
  };

  // Helper function to parse currency and numeric values
  const parseNumericValue = (rawValue, dataType) => {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return 0;
    }

    // Handle currency values (e.g., "₹35,000.00" -> 35000)
    if (dataType === 'currency' && typeof rawValue === 'string') {
      // Extract numeric value from currency string - remove ₹, $, commas
      const numericValue = parseFloat(rawValue.replace(/[₹$,]/g, ''));
      console.log(`WidgetBuilder - Currency conversion: "${rawValue}" -> ${numericValue}`);
      return isNaN(numericValue) ? 0 : numericValue;
    } else if (dataType === 'number' || dataType === 'decimal') {
      // Handle regular numeric values
      const numericValue = parseFloat(rawValue);
      console.log(`WidgetBuilder - Numeric conversion: "${rawValue}" -> ${numericValue}`);
      return isNaN(numericValue) ? 0 : numericValue;
    } else {
      // Fallback for other data types
      return parseFloat(rawValue) || 0;
    }
  };

  // Calculate aggregation (same as ReportDataViewer with currency support)
  const calculateAggregation = (rows, field, aggregation) => {
    if (!rows || rows.length === 0) return 0;

    const tableName = field.object_name || 'main';
    const aliasedFieldName = `${tableName}_${field.field_name}`;

    const values = rows.map(row => {
      const fieldData = row[aliasedFieldName] || row[field.field_name];
      if (fieldData) {
        let rawValue;
        if (typeof fieldData === 'object' && fieldData !== null) {
          rawValue = fieldData.value;
        } else {
          rawValue = fieldData;
        }

        // Use the helper function to parse numeric values
        return parseNumericValue(rawValue, field.data_type);
      }
      return 0;
    }).filter(val => !isNaN(val));

    if (values.length === 0) return 0;

    switch (aggregation.toUpperCase()) {
      case 'SUM':
        return values.reduce((sum, val) => sum + val, 0);
      case 'AVG':
      case 'AVERAGE':
        return values.reduce((sum, val) => sum + val, 0) / values.length;
      case 'MIN':
        return Math.min(...values);
      case 'MAX':
        return Math.max(...values);
      case 'COUNT':
        return values.length;
      default:
        return values.reduce((sum, val) => sum + val, 0);
    }
  };

  // Transform chart data to match ChartDisplay expected format
  const transformChartForDisplay = (chart) => {
    console.log('WidgetBuilder - Transforming chart for display:', chart);

    // Convert underscore field names to camelCase for ChartDisplay compatibility
    const transformedChart = {
      ...chart,
      // Convert chart_type to chartType
      chartType: chart.chart_type || chart.chartType,
      // Convert field names to camelCase
      xAxisField: chart.x_axis_field || chart.xAxisField,
      yAxisField: chart.y_axis_field || chart.yAxisField,
      yAxisFields: chart.y_axis_fields || chart.yAxisFields,
      groupField: chart.group_field || chart.groupField,
      pieValueField: chart.pie_value_field || chart.pieValueField,
      pieSliceField: chart.pie_slice_field || chart.pieSliceField,
      // Scatter chart fields
      scatterXAxis: chart.scatter_x_axis || chart.scatterXAxis,
      scatterYAxis: chart.scatter_y_axis || chart.scatterYAxis,
      scatterGroupField: chart.scatter_group_field || chart.scatterGroupField,
      scatterXAxisLabel: chart.scatter_x_axis_label || chart.scatterXAxisLabel,
      scatterYAxisLabel: chart.scatter_y_axis_label || chart.scatterYAxisLabel,
      // Other fields
      legendPosition: chart.legend_position || chart.legendPosition || 'top',
      showTooltips: chart.show_tooltips !== undefined ? chart.show_tooltips : (chart.showTooltips !== undefined ? chart.showTooltips : true),
      showLabels: chart.show_labels !== undefined ? chart.show_labels : (chart.showLabels !== undefined ? chart.showLabels : true),
      labelColor: chart.label_color || chart.labelColor,
      xAxisLabel: chart.x_axis_label || chart.xAxisLabel,
      yAxisLabel: chart.y_axis_label || chart.yAxisLabel,
      // Field colors
      fieldColors: chart.field_colors || chart.fieldColors
    };

    console.log('WidgetBuilder - Transformed chart:', transformedChart);
    return transformedChart;
  };

  const handleChartSelect = async (chart) => {
    console.log('WidgetBuilder - Chart selected:', chart);
    console.log('WidgetBuilder - Current selectedFields:', selectedFields);
    console.log('WidgetBuilder - Current summarizedFields:', summarizedFields);
    console.log('WidgetBuilder - Current selectedGroups:', selectedGroups);

    setSelectedChart(chart);
    setError(null);

    const chartConfig = {
      ...chart,
      chartType: chart.chartType || chart.chart_type,
      title: chart.title,
      xAxisField: chart.xAxisField || chart.x_axis_field,
      yAxisField: chart.yAxisField || chart.y_axis_field,
      yAxisFields: chart.yAxisFields || chart.y_axis_fields,
      groupField: chart.groupField || chart.group_field,
      pieValueField: chart.pieValueField || chart.pie_value_field,
      pieSliceField: chart.pieSliceField || chart.pie_slice_field,
      scatterXAxis: chart.scatterXAxis || chart.scatter_x_axis,
      scatterYAxis: chart.scatterYAxis || chart.scatter_y_axis,
      scatterGroupField: chart.scatterGroupField || chart.scatter_group_field,
      scatterXAxisLabel: chart.scatterXAxisLabel || chart.scatter_x_axis_label,
      scatterYAxisLabel: chart.scatterYAxisLabel || chart.scatter_y_axis_label,
      legendPosition: chart.legendPosition || chart.legend_position,
      showTooltips: chart.showTooltips !== undefined ? chart.showTooltips : chart.show_tooltips,
      showLabels: chart.showLabels !== undefined ? chart.showLabels : chart.show_labels,
      labelColor: chart.labelColor || chart.label_color,
      xAxisLabel: chart.xAxisLabel || chart.x_axis_label,
      yAxisLabel: chart.yAxisLabel || chart.y_axis_label,
      fieldColors: chart.fieldColors || chart.field_colors,
      id: chart.id,
      createdby: chart.createdby,
      createdbyid: chart.createdbyid,
      createddate: chart.createddate,
      lastmodifiedby: chart.lastmodifiedby,
      lastmodifiedbyid: chart.lastmodifiedbyid,
      lastmodifieddate: chart.lastmodifieddate
    };

    console.log('WidgetBuilder - Chart configuration being set:', {
      originalChart: chart,
      processedChartConfig: chartConfig,
      chartType: chartConfig.chartType,
      scatterXAxis: chartConfig.scatterXAxis,
      scatterYAxis: chartConfig.scatterYAxis,
      scatterGroupField: chartConfig.scatterGroupField
    });

    setWidgetConfig(prev => ({
      ...prev,
      name: prev.name || `${chart.title || 'Chart'} Widget`,
      chart_type: chartConfig.chartType,
      chart_config: chartConfig,
      type: 'chart'
    }));
  };

  const handleCreateNewChart = () => {
    setShowChartBuilder(true);
  };

  const handleChartCreated = (newChart) => {
    setSelectedChart(newChart);
    setShowChartBuilder(false);
    setWidgetConfig(prev => ({
      ...prev,
      name: prev.name || `${newChart.title || 'Chart'} Widget`,
      chart_type: newChart.chartType || newChart.chart_type,
      chart_config: newChart,
      type: 'chart'
    }));
    setCurrentStep(getConfigStep('report', false));
  };

  const handleIncludeFullReportToggle = (value) => {
    setIncludeFullReport(value);

    if (value) {
      setSelectedChart(null);
      setWidgetConfig(prev => ({
        ...prev,
        chart_type: 'report',
        chart_config: null,
        type: 'report'
      }));
      setCurrentStep(3);
    } else {
      setWidgetConfig(prev => ({
        ...prev,
        chart_type: 'bar',
        chart_config: null,
        type: 'chart'
      }));
      setSelectedChart(null);
      setCurrentStep(3);
    }
  };

  const handleConfigChange = (field, value) => {
    setWidgetConfig(prev => ({ ...prev, [field]: value }));
  };

  const handlePreview = async () => {
    try {
      setLoading(true);

      const isDashboardSelection = widgetType === 'dashboard';
      const isFullReportSelection = widgetType === 'report' && includeFullReport;
      const isChartSelection = widgetType === 'report' && !includeFullReport;

      if (isDashboardSelection && !selectedDashboard) {
        setError('Please select a dashboard before continuing.');
        setLoading(false);
        return;
      }

      if (!isDashboardSelection && !selectedReport) {
        setError('Please select a report before continuing.');
        setLoading(false);
        return;
      }

      if (isChartSelection && !selectedChart) {
        setError('Please select a chart before continuing.');
        setLoading(false);
        return;
      }

      const parentId = isDashboardSelection ? selectedDashboard?.id : selectedReport?.id;
      const reportLabel = selectedReport?.report_label || selectedReport?.name || 'Report';

      const defaultName = isDashboardSelection
        ? `${selectedDashboard?.name || 'Dashboard'} Widget`
        : isFullReportSelection
          ? `${reportLabel} - Full Report`
          : (selectedChart?.title || `${reportLabel} - ${getChartTypeLabel(selectedChart?.chart_type || widgetConfig.chart_type)}`);

      const widgetName = widgetConfig.name || defaultName;

      const widgetPreviewData = {
        id: editingWidget ? editingWidget.id : `preview-${Date.now()}`,
        name: widgetName,
        type: isDashboardSelection ? 'dashboard' : (isFullReportSelection ? 'report' : 'chart'),
        parent_id: parentId,
        chart_type: isDashboardSelection ? 'dashboard' : (isFullReportSelection ? 'report' : (selectedChart?.chartType || selectedChart?.chart_type || widgetConfig.chart_type)),
        column_span: widgetConfig.column_span || 6,
        row_span: widgetConfig.row_span || 4,
        refresh_rate: widgetConfig.refresh_rate,
        show_filters: widgetConfig.show_filters,
        show_export: widgetConfig.show_export,
        sort_order: widgetConfig.sort_order,
        chart_id: isChartSelection ? selectedChart?.id : null,
        chart_config: isChartSelection ? (widgetConfig.chart_config || selectedChart || {}) : null,
        report_label: reportLabel,
        dashboard_label: isDashboardSelection ? (selectedDashboard?.name || widgetName) : undefined,
        dashboard_description: isDashboardSelection ? selectedDashboard?.description : undefined,
        dataset: isChartSelection ? (editingWidget ? editingWidget.dataset : undefined) : null,
        previewData: isChartSelection ? (editingWidget ? (editingWidget.previewData || previewData) : previewData) : [],
        summarizedData: isChartSelection ? (editingWidget ? (editingWidget.summarizedData || editingWidget.dataset || summarizedData) : summarizedData) : null,
        groupingInfo: isChartSelection ? (editingWidget ? (editingWidget.groupingInfo || groupingInfo) : groupingInfo) : null,
        selectedFields: isChartSelection ? (editingWidget ? (editingWidget.selectedFields || selectedFields) : selectedFields) : [],
        summarizedFields: isChartSelection ? (editingWidget ? (editingWidget.summarizedFields || summarizedFields) : summarizedFields) : {},
        selectedGroups: isChartSelection ? (editingWidget ? (editingWidget.selectedGroups || selectedGroups) : selectedGroups) : [],
        isPreview: !editingWidget,
        x: editingWidget ? editingWidget.x : 0,
        y: editingWidget ? editingWidget.y : 0,
        selectedChart: isChartSelection ? selectedChart : null,
        selectedDashboard: isDashboardSelection ? selectedDashboard : null,
        selectedReport: selectedReport
      };

      if (onPreviewWidget) {
        await onPreviewWidget(widgetPreviewData);
      }

      onClose();

    } catch (error) {
      console.error('WidgetBuilder - Error creating/updating widget:', error);
      setError('Failed to create/update widget');
    } finally {
      setLoading(false);
    }
  };

  const handleWidgetTypeSelect = (type) => {
    setWidgetType(type);
    setSelectedDashboard(null);
    setSelectedReport(null);
    setSelectedChart(null);
    setError(null);
    setSearchTerm('');
    setDashboardSearchTerm('');
    setWidgetConfig(prev => ({
      ...prev,
      name: '',
      chart_type: type === 'dashboard' ? 'dashboard' : 'bar',
      chart_config: type === 'dashboard' ? null : {},
      type: type === 'dashboard' ? 'dashboard' : 'report'
    }));
    if (type === 'dashboard') {
      setIncludeFullReport(false);
      if (dashboards.length === 0) {
        loadDashboards();
      }
    } else {
      setIncludeFullReport(false);
      if (reports.length === 0) {
        loadReports();
      }
    }
    setCurrentStep(2);
  };

  const handleDashboardSelect = (dashboard) => {
    setSelectedDashboard(dashboard);
    setError(null);
    setWidgetConfig(prev => ({
      ...prev,
      name: prev.name || `${dashboard.name} Widget`,
      chart_type: 'dashboard',
      chart_config: null,
      type: 'dashboard'
    }));
    setCurrentStep(3);
  };


  const getChartIcon = (chartType) => {
    const normalizedType = (chartType || '').toLowerCase();
    switch (normalizedType) {
      case 'line':
        return <FaChartLine />;
      case 'pie':
      case 'donut':
        return <FaChartPie />;
      case 'area':
        return <FaChartArea />;
      case 'table':
        return <FaTable />;
      default:
        return <FaChartBar />;
    }
  };

  const getChartTypeLabel = (chartType) => {
    switch (chartType) {
      case 'bar':
        return 'Bar Chart';
      case 'line':
        return 'Line Chart';
      case 'donut':
        return 'Pie Chart';
      case 'area':
        return 'Area Chart';
      case 'table':
        return 'Table';
      default:
        return 'Chart';
    }
  };

  const renderChartSelectionStep = () => (
    <div className="builder-step">
      <div className="step-header">
        {!editingWidget && (
          <button
            className="back-btn"
            onClick={() => setCurrentStep(2)}
          >
            <FaArrowLeft />
            Back
          </button>
        )}
        <h3>Choose Chart Type</h3>
        <p>Select an existing chart or create a new one</p>
      </div>

      {selectedReport && (
        <div className="selected-report">
          <div className="report-summary">
            <FaChartBar className="report-icon" />
            <div>
              <h4>{selectedReport.report_label || selectedReport.name}</h4>
              <p>Selected Report</p>
            </div>
          </div>
        </div>
      )}

      {widgetType === 'report' && (
        <div className="widget-report-options">
          <div className="widget-report-mode-toggle">
            <button
              type="button"
              className={`widget-report-mode-btn ${includeFullReport ? 'active' : ''}`}
              onClick={() => handleIncludeFullReportToggle(true)}
            >
              <span className="widget-report-mode-title">Full Report</span>
              <span className="widget-report-mode-subtitle">Embed complete report viewer</span>
            </button>
            <button
              type="button"
              className={`widget-report-mode-btn ${!includeFullReport ? 'active' : ''}`}
              onClick={() => handleIncludeFullReportToggle(false)}
            >
              <span className="widget-report-mode-title">Specific Chart</span>
              <span className="widget-report-mode-subtitle">Pick from report charts</span>
            </button>
          </div>

          {includeFullReport && (
            <p className="widget-report-help-text">
              The widget will render the entire report using the standard report viewer. Sort order and sizing can be adjusted in the next step.
            </p>
          )}
        </div>
      )}

      {!includeFullReport && (
        <div className="chart-options">
          <div className="existing-charts">
            <h4>Existing Charts</h4>
            {reportCharts.length === 0 ? (
              <div className="no-charts">
                <p>No existing charts found for this report</p>
              </div>
            ) : (
              <div className="charts-list">
                {reportCharts.map((chart) => (
                  <div
                    key={chart.id}
                    className="chart-preview-item"
                    onClick={() => handleChartSelect(chart)}
                  >
                    <div className="chart-preview-header">
                      <div className="chart-icon">
                        {getChartIcon(chart.chart_type)}
                      </div>
                      <div className="chart-info">
                        <h5>{chart.title || 'Untitled Chart'}</h5>
                        <p>{getChartTypeLabel(chart.chart_type)}</p>
                      </div>
                      <div className="chart-select-btn">
                        <FaArrowRight />
                      </div>
                    </div>
                    <div className="chart-preview-content">
                      {previewLoading ? (
                        <div className="loading-state">
                          <div className="loading-spinner"></div>
                          <p>Loading chart data...</p>
                        </div>
                      ) : previewData.length > 0 && summarizedData && Object.keys(summarizedData).length > 0 ? (
                        <div className="chart-preview">
                          <ChartDisplay
                            chart={transformChartForDisplay(chart)}
                            selectedFields={selectedFields}
                            summarizedFields={summarizedFields}
                            selectedGroups={selectedGroups}
                            summarizedData={summarizedData}
                            previewData={previewData}
                            isPreview={true}
                            displayAuditInfo={false}
                          />
                        </div>
                      ) : (
                        <div className="chart-preview-placeholder">
                          <div className="placeholder-icon">
                            {getChartIcon(chart.chart_type)}
                          </div>
                          <p>Chart Preview</p>
                          <small>{chart.title || 'Untitled Chart'}</small>
                          <div className="chart-config-info">
                            <div className="config-item">
                              <strong>Type:</strong> {getChartTypeLabel(chart.chart_type)}
                            </div>
                            {chart.x_axis_field && (
                              <div className="config-item">
                                <strong>X-Axis:</strong> {chart.x_axis_field}
                              </div>
                            )}
                            {chart.y_axis_field && (
                              <div className="config-item">
                                <strong>Y-Axis:</strong> {chart.y_axis_field}
                              </div>
                            )}
                            {chart.group_field && (
                              <div className="config-item">
                                <strong>Group:</strong> {chart.group_field}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderConfigStep = () => {
    const backTarget = widgetType === 'dashboard' ? 2 : widgetType === 'report' && includeFullReport ? 2 : 3;
    const showSelectedChartSummary = !includeFullReport && selectedChart && (selectedChart.chart_type || selectedChart.chartType);
    const columnOptions = widgetType === 'report'
      ? REPORT_COLUMN_OPTIONS
      : DASHBOARD_COLUMN_VALUES.map((value) => ({
          value,
          label: getDashboardColumnLabel(value)
        }));

    return (
      <div className="builder-step">
        <div className="step-header">
          {!editingWidget && (
            <button
              className="back-btn"
              onClick={() => setCurrentStep(backTarget)}
            >
              <FaArrowLeft />
              Back
            </button>
          )}
          <h3>{editingWidget ? 'Edit Widget' : 'Configure Widget'}</h3>
          <p>{editingWidget ? 'Modify the widget settings' : 'Set the widget size, name, and display options'}</p>
        </div>

        {widgetType === 'dashboard' && selectedDashboard && (
          <div className="selected-report">
            <div className="report-summary">
              <FaTachometerAlt className="report-icon" />
              <div>
                <h4>{selectedDashboard.name || selectedDashboard.label}</h4>
                <p>Selected Dashboard</p>
              </div>
            </div>
          </div>
        )}

        {widgetType === 'report' && includeFullReport && selectedReport && (
          <div className="full-report-summary">
            <div className="report-summary">
              <FaRegFileAlt className="report-icon" />
              <div>
                <h4>{selectedReport.report_label || selectedReport.name}</h4>
                <p>The full report viewer will be embedded in this widget.</p>
              </div>
            </div>
          </div>
        )}

        {showSelectedChartSummary && (() => {
          const chartType = (selectedChart.chartType || selectedChart.chart_type || '').toLowerCase();
          const chartName = selectedChart.name || `${chartType.charAt(0).toUpperCase() + chartType.slice(1)} Chart`;
          return (
            <div className="selected-chart">
              <div className="chart-summary">
                <div className="chart-icon">
                  {chartType === 'bar' && <FaChartBar />}
                  {chartType === 'line' && <FaChartLine />}
                  {chartType === 'pie' && <FaChartPie />}
                  {chartType === 'area' && <FaChartArea />}
                  {chartType === 'table' && <FaTable />}
                </div>
                <div>
                  <h4>{chartName}</h4>
                  <p>Selected Chart</p>
                </div>
              </div>
            </div>
          );
        })()}

        <div className="widget-config-form">
          <div className="config-section">
            <h4>Widget Settings</h4>

            <div className="form-group">
              <label htmlFor="widget-name">Widget Name</label>
              <input
                id="widget-name"
                type="text"
                value={widgetConfig.name}
                onChange={(e) => handleConfigChange('name', e.target.value)}
                placeholder="Enter widget name..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="column-span">Columns (Width)</label>
              <select
                id="column-span"
                value={widgetConfig.column_span}
                onChange={(e) => handleConfigChange('column_span', parseInt(e.target.value))}
              >
                {columnOptions.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="row-span">Rows (Height)</label>
              <select
                id="row-span"
                value={widgetConfig.row_span}
                onChange={(e) => handleConfigChange('row_span', parseInt(e.target.value))}
              >
                {ROW_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {getRowOptionLabel(value)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="sort-order">Sort Order</label>
              <input
                id="sort-order"
                type="number"
                min="1"
                value={widgetConfig.sort_order || ''}
                onChange={(e) => handleConfigChange('sort_order', parseInt(e.target.value) || '')}
                placeholder="Enter sort order number..."
              />
            </div>
          </div>

          <div className="config-actions">
            <button
              className="add-widget-btn"
              onClick={handlePreview}
              disabled={loading || (widgetType === 'dashboard' && !selectedDashboard) || (widgetType === 'report' && !includeFullReport && !selectedChart)}
            >
              {loading ? (
                <>
                  <div className="loading-spinner-small"></div>
                  {editingWidget ? 'Updating Widget...' : 'Adding Widget...'}
                </>
              ) : (
                <>
                  <FaPlus />
                  {editingWidget ? 'Update Widget' : 'Add Widget'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  // Get summarized data for charts (same as ReportDataViewer)
  const summarizedData = createSummarizedDataForCharts() || {};
  const steps = widgetType === 'report'
    ? ['Type', 'Select', 'Chart', 'Config']
    : ['Type', 'Select', 'Config'];
  const configStepIndex = steps.length;
  const headerTitle = editingWidget ? 'Edit Widget' : 'Add Widget';

  return (
    <div className="widget-builder-overlay">
      <div className="widget-builder">
        {/* Header */}
        <div className="builder-header">
          <div className="builder-title">
            <h2>{headerTitle}</h2>
            <button className="builder-close-btn" onClick={onClose} aria-label="Close widget builder">
              ×
            </button>
          </div>
          <div className="builder-steps">
            {steps.map((label, index) => (
              <div key={label} className={`step ${currentStep >= index + 1 ? 'active' : ''}`}>
                <span className="step-number">{index + 1}</span>
                <span className="step-label">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="builder-content">
          {error && (
            <div className="builder-error">
              <p>{error}</p>
              <button onClick={() => setError(null)}>×</button>
            </div>
          )}

          {/* Step 1: Choose Widget Type */}
          {currentStep === 1 && (
            <div className="builder-step">
              <div className="step-header">
                <h3>Choose Widget Type</h3>
                <p>Select the type of widget you want to add to the dashboard</p>
              </div>
              <div className="widget-type-options">
                <button
                  type="button"
                  className={`widget-type-option ${widgetType === 'report' ? 'active' : ''}`}
                  onClick={() => handleWidgetTypeSelect('report')}
                >
                  <div className="widget-type-icon report">
                    <FaChartBar />
                  </div>
                  <div className="widget-type-content">
                    <h4>Report Widget</h4>
                    <p>Embed a full report or one of its charts</p>
                  </div>
                </button>
                <button
                  type="button"
                  className={`widget-type-option ${widgetType === 'dashboard' ? 'active' : ''}`}
                  onClick={() => handleWidgetTypeSelect('dashboard')}
                >
                  <div className="widget-type-icon dashboard">
                    <FaChartPie />
                  </div>
                  <div className="widget-type-content">
                    <h4>Dashboard Widget</h4>
                    <p>Embed an existing dashboard inside this dashboard</p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Select Source */}
          {currentStep === 2 && widgetType === 'dashboard' && (
            <div className="builder-step">
              <div className="step-header">
                <button
                  className="back-btn"
                  onClick={() => setCurrentStep(1)}
                >
                  <FaArrowLeft />
                  Back
                </button>
                <h3>Choose a Dashboard</h3>
                <p>Select the dashboard that will be displayed as a widget</p>
              </div>

              <div className="search-section">
                <div className="search-box">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search dashboards..."
                    value={dashboardSearchTerm}
                    onChange={(e) => setDashboardSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="reports-list">
                {dashboardsLoading ? (
                  <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading dashboards...</p>
                  </div>
                ) : filteredDashboards.length === 0 ? (
                  <div className="empty-state">
                    <p>No dashboards found</p>
                  </div>
                ) : (
                  filteredDashboards.map((dashboard) => (
                    <div
                      key={dashboard.id}
                      className={`report-item ${selectedDashboard?.id === dashboard.id ? 'selected' : ''}`}
                      onClick={() => handleDashboardSelect(dashboard)}
                    >
                      <div className="report-icon dashboard">
                        <FaChartBar />
                      </div>
                      <div className="report-info">
                        <h4>{dashboard.name}</h4>
                        <p>Dashboard</p>
                      </div>
                      <div className="report-meta">
                        {dashboard.description && <span>{dashboard.description}</span>}
                      </div>
                      <div className="report-arrow">
                        <FaArrowRight />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {currentStep === 2 && widgetType === 'report' && (
            <div className="builder-step">
              <div className="step-header">
                {!editingWidget && (
                  <button
                    className="back-btn"
                    onClick={() => setCurrentStep(1)}
                  >
                    <FaArrowLeft />
                    Back
                  </button>
                )}
                <h3>Choose a Report</h3>
                <p>Select the report that will provide data for your widget</p>
              </div>

              <div className="search-section">
                <div className="search-box">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search reports..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="reports-list">
                {loading ? (
                  <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading reports...</p>
                  </div>
                ) : filteredReports.length === 0 ? (
                  <div className="empty-state">
                    <p>No reports found</p>
                  </div>
                ) : (
                  filteredReports.map((report) => (
                    <div
                      key={report.id}
                      className={`report-item ${selectedReport?.id === report.id ? 'selected' : ''}`}
                      onClick={() => handleReportSelect(report)}
                    >
                      <div className="report-icon">
                        <FaChartBar />
                      </div>
                      <div className="report-info">
                        <h4>{report.report_label || report.name}</h4>
                        <p>{report.description || 'Report'}</p>
                      </div>
                      <div className="report-arrow">
                        <FaArrowRight />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Step 3: Report presentation options */}
          {currentStep === 3 && widgetType === 'report' && (
            <div className="builder-step">
              <div className="step-header">
                <button
                  className="back-btn"
                  onClick={() => setCurrentStep(2)}
                >
                  <FaArrowLeft />
                  Back
                </button>
                <h3>Choose Widget Content</h3>
                <p>Embed the full report viewer or pick a specific chart</p>
              </div>

              {selectedReport && (
                <div className="selected-report">
                  <div className="report-summary">
                    <FaChartBar className="report-icon" />
                    <div>
                      <h4>{selectedReport.report_label || selectedReport.name}</h4>
                      <p>Selected Report</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="widget-report-options">
                <div className="widget-report-mode-toggle">
                  <button
                    type="button"
                    className={`widget-report-mode-btn ${includeFullReport ? 'active' : ''}`}
                    onClick={() => handleIncludeFullReportToggle(true)}
                  >
                    <span className="widget-report-mode-title">Full Report</span>
                    <span className="widget-report-mode-subtitle">Embed complete report viewer</span>
                  </button>
                  <button
                    type="button"
                    className={`widget-report-mode-btn ${!includeFullReport ? 'active' : ''}`}
                    onClick={() => handleIncludeFullReportToggle(false)}
                  >
                    <span className="widget-report-mode-title">Specific Chart</span>
                    <span className="widget-report-mode-subtitle">Pick from report charts</span>
                  </button>
                </div>

                {includeFullReport && (
                  <p className="widget-report-help-text">
                    The widget will render the entire report using the standard report viewer. Sort order and sizing can be adjusted in the next step.
                  </p>
                )}

                {!includeFullReport && (
                  <div className="chart-options">
                    <div className="existing-charts">
                      <h4>Existing Charts</h4>
                      {reportCharts.length === 0 ? (
                        <div className="no-charts">
                          <p>No existing charts found for this report</p>
                        </div>
                      ) : (
                        <div className="charts-list">
                          {reportCharts.map((chart) => (
                            <div
                              key={chart.id}
                              className={`chart-preview-item ${selectedChart?.id === chart.id ? 'selected' : ''}`}
                              onClick={() => handleChartSelect(chart)}
                            >
                              <div className="chart-preview-header">
                                <div className="chart-icon">
                                  {getChartIcon(chart.chart_type)}
                                </div>
                                <div className="chart-info">
                                  <h5>{chart.title || 'Untitled Chart'}</h5>
                                  <p>{getChartTypeLabel(chart.chart_type)}</p>
                                </div>
                                <div className="chart-select-btn">
                                  <FaArrowRight />
                                </div>
                              </div>
                              <div className="chart-preview-content">
                                {previewLoading ? (
                                  <div className="loading-state">
                                    <div className="loading-spinner"></div>
                                    <p>Loading chart data...</p>
                                  </div>
                                ) : previewData.length > 0 && summarizedData && Object.keys(summarizedData).length > 0 ? (
                                  <div className="chart-preview">
                                    <ChartDisplay
                                      chart={transformChartForDisplay(chart)}
                                      selectedFields={selectedFields}
                                      summarizedFields={summarizedFields}
                                      selectedGroups={selectedGroups}
                                      summarizedData={summarizedData}
                                      previewData={previewData}
                                      isPreview={true}
                                      displayAuditInfo={false}
                                    />
                                  </div>
                                ) : (
                                  <div className="chart-preview-placeholder">
                                    <div className="placeholder-icon">
                                      {getChartIcon(chart.chart_type)}
                                    </div>
                                    <p>Chart Preview</p>
                                    <small>{chart.title || 'Untitled Chart'}</small>
                                    <div className="chart-config-info">
                                      <div className="config-item">
                                        <strong>Type:</strong> {getChartTypeLabel(chart.chart_type)}
                                      </div>
                                      {chart.x_axis_field && (
                                        <div className="config-item">
                                          <strong>X-Axis:</strong> {chart.x_axis_field}
                                        </div>
                                      )}
                                      {chart.y_axis_field && (
                                        <div className="config-item">
                                          <strong>Y-Axis:</strong> {chart.y_axis_field}
                                        </div>
                                      )}
                                      {chart.group_field && (
                                        <div className="config-item">
                                          <strong>Group:</strong> {chart.group_field}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

            </div>
          )}

          {/* Step 3 or 4: Widget Configuration */}
          {currentStep === configStepIndex && (
            <div className="builder-step">
              <div className="step-header">
                {!editingWidget && currentStep > 1 && (
                  <button
                    className="back-btn"
                    onClick={() => setCurrentStep(Math.max(1, configStepIndex - 1))}
                  >
                    <FaArrowLeft />
                    Back
                  </button>
                )}
                <h3>{editingWidget ? 'Edit Widget' : 'Configure Widget'}</h3>
                <p>{editingWidget ? 'Modify the widget settings' : 'Set the widget size, name, and display options'}</p>
              </div>

              {widgetType === 'dashboard' && selectedDashboard && (
                <div className="selected-chart">
                  <div className="chart-summary">
                    <div className="chart-icon">
                      <FaChartBar />
                    </div>
                    <div>
                      <h4>{selectedDashboard.name}</h4>
                      <p>Selected Dashboard</p>
                    </div>
                  </div>
                </div>
              )}

              {widgetType === 'report' && includeFullReport && selectedReport && (
                <div className="full-report-summary">
                  <div className="report-summary">
                    <FaChartBar className="report-icon" />
                    <div>
                      <h4>{selectedReport.report_label || selectedReport.name}</h4>
                      <p>The full report viewer will be embedded in this widget.</p>
                    </div>
                  </div>
                </div>
              )}

              {widgetType === 'report' && !includeFullReport && selectedChart && (
                <div className="selected-chart">
                  <div className="chart-summary">
                    <div className="chart-icon">
                      {selectedChart.chartType === 'bar' && <FaChartBar />}
                      {selectedChart.chartType === 'line' && <FaChartLine />}
                      {selectedChart.chartType === 'pie' && <FaChartPie />}
                      {selectedChart.chartType === 'area' && <FaChartArea />}
                      {selectedChart.chartType === 'table' && <FaTable />}
                    </div>
                    <div>
                      <h4>{selectedChart.name || `${selectedChart.chartType.charAt(0).toUpperCase() + selectedChart.chartType.slice(1)} Chart`}</h4>
                      <p>Selected Chart</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="widget-config-form">
                <div className="config-section">
                  <h4>Widget Settings</h4>

                  <div className="form-group">
                    <label htmlFor="widget-name">Widget Name</label>
                    <input
                      id="widget-name"
                      type="text"
                      value={widgetConfig.name}
                      onChange={(e) => handleConfigChange('name', e.target.value)}
                      placeholder="Enter widget name..."
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="column-span">Columns (Width)</label>
                    <select
                      id="column-span"
                      value={widgetConfig.column_span}
                      onChange={(e) => handleConfigChange('column_span', parseInt(e.target.value))}
                    >
                      {DASHBOARD_COLUMN_VALUES.map((value) => (
                        <option key={value} value={value}>
                          {getDashboardColumnLabel(value)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="row-span">Rows (Height)</label>
                    <select
                      id="row-span"
                      value={widgetConfig.row_span}
                      onChange={(e) => handleConfigChange('row_span', parseInt(e.target.value))}
                    >
                      {ROW_OPTIONS.map((value) => (
                        <option key={value} value={value}>
                          {getRowOptionLabel(value)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="sort-order">Sort Order</label>
                    <input
                      id="sort-order"
                      type="number"
                      min="1"
                      value={widgetConfig.sort_order || ''}
                      onChange={(e) => handleConfigChange('sort_order', parseInt(e.target.value) || '')}
                      placeholder="Enter sort order number..."
                    />
                  </div>
                </div>

                <div className="config-actions">
                  <button
                    className="add-widget-btn"
                    onClick={handlePreview}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="loading-spinner-small"></div>
                        {editingWidget ? 'Updating Widget...' : 'Adding Widget...'}
                      </>
                    ) : (
                      <>
                        <FaPlus />
                        {editingWidget ? 'Update Widget' : 'Add Widget'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="builder-footer">
          <div className="footer-actions">
            <div className="footer-buttons">
              <button
                className="cancel-btn"
                onClick={onClose}
              >
                Cancel
              </button>
              {currentStep === 3 && widgetType === 'report' && (
                <button
                  className="footer-next-btn"
                  onClick={() => setCurrentStep(configStepIndex)}
                  disabled={!includeFullReport && !selectedChart}
                >
                  Next
                  <FaArrowRight />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WidgetBuilder;
